import { z } from 'zod';

// Search query validation - prevent extremely long inputs
export const searchQuerySchema = z.string()
  .max(200, 'Search query too long')
  .transform(val => val.trim());

// Email validation
export const emailSchema = z.string()
  .email('Invalid email format')
  .max(255, 'Email too long')
  .transform(val => val.toLowerCase().trim());

// File upload validation (images only)
export const imageFileSchema = z.custom<File>()
  .refine((file) => file instanceof File, 'Must be a file')
  .refine((file) => file.size <= 10 * 1024 * 1024, 'File too large (max 10MB)')
  .refine(
    (file) => ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'].includes(file.type),
    'Invalid file type - must be an image (JPEG, PNG, GIF, WebP, or HEIC)'
  );

// File upload validation (images + documents)
const ALLOWED_DOC_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const documentFileSchema = z.custom<File>()
  .refine((file) => file instanceof File, 'Must be a file')
  .refine((file) => file.size <= 20 * 1024 * 1024, 'File too large (max 20MB)')
  .refine(
    (file) => ALLOWED_DOC_TYPES.includes(file.type),
    'Invalid file type - must be an image, PDF, or Word document'
  );

// Password validation for signup
export const passwordSchema = z.string()
  .min(6, 'Password must be at least 6 characters')
  .max(72, 'Password must be less than 72 characters');

// Generic text field validation
export const textFieldSchema = (maxLength: number = 255) => z.string()
  .max(maxLength, `Must be less than ${maxLength} characters`)
  .transform(val => val.trim());

// City name validation
export const citySchema = z.string()
  .min(1, 'City is required')
  .max(100, 'City name too long')
  .transform(val => val.trim());

// Address validation
export const addressSchema = z.string()
  .min(1, 'Address is required')
  .max(500, 'Address too long')
  .transform(val => val.trim());

// Validate an array of files for image upload
export function validateImageFiles(files: FileList | File[]): { valid: File[]; errors: string[] } {
  const fileArray = Array.from(files);
  const valid: File[] = [];
  const errors: string[] = [];

  for (const file of fileArray) {
    const result = imageFileSchema.safeParse(file);
    if (result.success) {
      valid.push(file);
    } else {
      errors.push(`${file.name}: ${result.error.errors[0]?.message || 'Invalid file'}`);
    }
  }

  return { valid, errors };
}

// Validate document files (images + PDF + doc)
export function validateDocumentFiles(files: FileList | File[]): { valid: File[]; errors: string[] } {
  const fileArray = Array.from(files);
  const valid: File[] = [];
  const errors: string[] = [];

  for (const file of fileArray) {
    const result = documentFileSchema.safeParse(file);
    if (result.success) {
      valid.push(file);
    } else {
      errors.push(`${file.name}: ${result.error.errors[0]?.message || 'Invalid file'}`);
    }
  }

  return { valid, errors };
}

// Helper to check if a file path is a PDF
export function isFilePdf(path: string): boolean {
  return path.toLowerCase().endsWith('.pdf');
}

// Helper to check if a file path is a document (non-image)
export function isFileDocument(path: string): boolean {
  const ext = path.toLowerCase().split('.').pop() || '';
  return ['pdf', 'doc', 'docx'].includes(ext);
}

// Validate search query safely
export function validateSearchQuery(query: string): string | null {
  const result = searchQuerySchema.safeParse(query);
  return result.success ? result.data : null;
}

// Validate email safely
export function validateEmail(email: string): string | null {
  const result = emailSchema.safeParse(email);
  return result.success ? result.data : null;
}
